import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Task, Project } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Clock, Zap, Check, SkipForward } from "lucide-react";
import { VoiceInputButton } from "@/components/common/VoiceInput";
import { TimeInput } from "@/components/ui/time-input";
import { formatCompactTime } from "@/lib/timeUtils";

interface DailyQuickLogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface EffortEntry {
  taskId: number;
  hours: string;
  description: string;
  workLocation: string;
  enabled: boolean;
}

export default function DailyQuickLog({ open, onOpenChange }: DailyQuickLogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState<{ hours: number; tasks: number }>({ hours: 0, tasks: 0 });

  const defaultLocation = localStorage.getItem("quicklog_default_location") || "office";

  const { data: tasks = [] } = useQuery<Task[]>({
    queryKey: user?.id ? [`/api/tasks/assignee/${user.id}`] : [],
    enabled: !!user?.id && open,
  });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
    enabled: open,
  });

  // Filter to active tasks only
  const activeTasks = useMemo(() => {
    return tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled' && !t.deletedAt);
  }, [tasks]);

  // Group tasks by project
  const tasksByProject = useMemo(() => {
    const groups: Record<number, { project: Project | undefined; tasks: Task[] }> = {};
    for (const task of activeTasks) {
      if (!groups[task.projectId]) {
        groups[task.projectId] = {
          project: projects.find(p => p.id === task.projectId),
          tasks: [],
        };
      }
      groups[task.projectId].tasks.push(task);
    }
    return Object.values(groups);
  }, [activeTasks, projects]);

  // Initialize entries for each active task
  const [entries, setEntries] = useState<Record<number, EffortEntry>>({});

  // Reset entries when tasks change
  useMemo(() => {
    const initial: Record<number, EffortEntry> = {};
    for (const task of activeTasks) {
      initial[task.id] = entries[task.id] || {
        taskId: task.id,
        hours: "",
        description: "",
        workLocation: defaultLocation,
        enabled: false,
      };
    }
    setEntries(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTasks.length]);

  const updateEntry = (taskId: number, field: keyof EffortEntry, value: string | boolean) => {
    setEntries(prev => ({
      ...prev,
      [taskId]: { ...prev[taskId], [field]: value, enabled: field === "hours" || field === "description" ? true : prev[taskId].enabled },
    }));
  };

  const activeEntries = useMemo(() => {
    return Object.values(entries).filter(e => e.hours && parseFloat(e.hours) > 0 && e.description.length >= 10);
  }, [entries]);

  const totalHours = useMemo(() => {
    return activeEntries.reduce((sum, e) => sum + parseFloat(e.hours || "0"), 0);
  }, [activeEntries]);

  const bulkMutation = useMutation({
    mutationFn: async (payload: any[]) => {
      return apiRequest("/api/task-efforts/bulk", {
        method: "POST",
        body: { entries: payload } as any,
      });
    },
    onSuccess: (data) => {
      const count = Array.isArray(data) ? data.length : 0;
      setSubmittedSummary({ hours: totalHours, tasks: count });
      setSubmitted(true);

      // Set localStorage so modal doesn't show again today
      if (user?.id) {
        localStorage.setItem(`quicklog_last_shown_${user.id}`, format(new Date(), "yyyy-MM-dd"));
      }

      queryClient.invalidateQueries({ queryKey: ["/api/task-efforts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/summary"] });

      toast({
        title: `${formatCompactTime(totalHours)} logged across ${count} task${count !== 1 ? "s" : ""}!`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to log efforts",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    const today = format(new Date(), "yyyy-MM-dd");
    const payload = activeEntries.map(e => ({
      taskId: e.taskId,
      hours: e.hours,
      description: e.description,
      workLocation: e.workLocation,
      effortDate: today,
      userId: user?.id,
    }));
    bulkMutation.mutate(payload);
  };

  const handleSkip = () => {
    if (user?.id) {
      localStorage.setItem(`quicklog_last_shown_${user.id}`, format(new Date(), "yyyy-MM-dd"));
    }
    onOpenChange(false);
  };

  const handleClose = () => {
    setSubmitted(false);
    onOpenChange(false);
  };

  const handleLocationDefault = (loc: string) => {
    localStorage.setItem("quicklog_default_location", loc);
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">Hours Logged!</h3>
            <p className="text-muted-foreground">
              {formatCompactTime(submittedSummary.hours)} logged across {submittedSummary.tasks} task{submittedSummary.tasks !== 1 ? "s" : ""}
            </p>
          </div>
          <DialogFooter>
            <Button onClick={handleClose} className="w-full">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Daily Quick Log
          </DialogTitle>
          <DialogDescription>
            Log your hours for today across your assigned tasks
          </DialogDescription>
        </DialogHeader>

        {activeTasks.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No active tasks assigned to you</p>
          </div>
        ) : (
          <div className="space-y-6">
            {tasksByProject.map(({ project, tasks: groupTasks }) => (
              <div key={project?.id || "unknown"}>
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  {project?.name || "Unknown Project"}
                </h4>
                <div className="space-y-3">
                  {groupTasks.map(task => {
                    const entry = entries[task.id];
                    if (!entry) return null;
                    const hasContent = entry.hours || entry.description;
                    const isValid = entry.hours && parseFloat(entry.hours) >= 0.5 && entry.description.length >= 10;

                    return (
                      <div
                        key={task.id}
                        className={`p-3 rounded-lg border transition-colors ${
                          hasContent ? (isValid ? "border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30" : "border-amber-200 bg-amber-50/30 dark:border-amber-800 dark:bg-amber-950/20") : "border-border"
                        }`}
                      >
                        <p className="text-sm font-medium text-foreground mb-2 truncate">{task.title}</p>
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-3">
                            <TimeInput
                              variant="compact"
                              showHint={false}
                              value={entry.hours}
                              onChange={(val) => updateEntry(task.id, "hours", val)}
                              minHours={0.25}
                              maxHours={16}
                            />
                          </div>
                          <div className="col-span-5 relative">
                            <Input
                              placeholder="What did you work on? (min 10 chars)"
                              value={entry.description}
                              onChange={e => updateEntry(task.id, "description", e.target.value)}
                              className={`text-sm h-8 pr-8 ${entry.description.length > 0 && entry.description.length < 10 ? "border-destructive" : ""}`}
                              maxLength={500}
                            />
                            <div className="absolute top-0.5 right-0.5">
                              <VoiceInputButton
                                variant="inline"
                                onTranscript={(text) => updateEntry(task.id, "description", entry.description + (entry.description ? " " : "") + text)}
                              />
                            </div>
                          </div>
                          <div className="col-span-4">
                            <Select
                              value={entry.workLocation}
                              onValueChange={val => {
                                updateEntry(task.id, "workLocation", val);
                                handleLocationDefault(val);
                              }}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="office">Office</SelectItem>
                                <SelectItem value="home">Home</SelectItem>
                                <SelectItem value="client_site">Client Site</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2 pt-4 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {activeEntries.length > 0 && (
              <span className="font-medium text-foreground">
                {formatCompactTime(totalHours)} across {activeEntries.length} task{activeEntries.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleSkip} className="gap-1">
              <SkipForward className="h-4 w-4" />
              Skip for now
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={activeEntries.length === 0 || bulkMutation.isPending}
              className="gap-1"
            >
              <Clock className="h-4 w-4" />
              {bulkMutation.isPending ? "Logging..." : "Log All"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
